import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, PackageOpen, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import ProductCard from "@/components/ProductCard";
import SectionHeading from "@/components/SectionHeading";
import { getProducts, getCategories } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const activeCat = params.get("category") || "all";

  useSeo({
    title: "Our Products | BIOSYN Aquaculture Solutions",
    description: "Browse the complete BIOSYN aquaculture product range—minerals, probiotics, gut health, vitamins, growth support, pond management and water quality solutions.",
  });

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    getProducts(activeCat === "all" ? {} : { category: activeCat })
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [activeCat]);

  const filtered = useMemo(() => {
    if (!search.trim()) return products;
    const q = search.toLowerCase();
    return products.filter((p) =>
      [p.name, p.short_description, p.category_name, p.tagline].filter(Boolean).some((f) => f.toLowerCase().includes(q))
    );
  }, [products, search]);

  const setCat = (code) => {
    if (code === "all") setParams({});
    else setParams({ category: code });
  };

  return (
    <div>
      <section style={{ background: "#063B63" }} className="py-16 md:py-20">
        <div className="container-x">
          <SectionHeading light overline="Product Catalogue" title="Our Products"
            subtitle="Integrated, application-oriented solutions for healthier ponds and better culture performance." />
          <div className="mt-8 relative max-w-md">
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2" style={{ color: "#4A6B82" }} />
            <Input
              data-testid="product-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search BIOSYN products..."
              className="pl-12 h-12 bg-white border-0 rounded-full"
            />
          </div>
        </div>
      </section>

      <section className="section container-x">
        {/* Filters */}
        <div className="flex flex-wrap gap-2.5 mb-10">
          <button data-testid="filter-all" onClick={() => setCat("all")}
            className="px-4 py-2 rounded-full text-sm font-semibold transition-colors border"
            style={activeCat === "all"
              ? { background: "#087FBF", color: "#fff", borderColor: "#087FBF" }
              : { background: "#fff", color: "#17324D", borderColor: "rgba(8,127,191,0.2)" }}>
            All
          </button>
          {categories.map((c) => (
            <button key={c.code} data-testid={`filter-${c.code}`} onClick={() => setCat(c.code)}
              className="px-4 py-2 rounded-full text-sm font-semibold transition-colors border"
              style={activeCat === c.code
                ? { background: "#087FBF", color: "#fff", borderColor: "#087FBF" }
                : { background: "#fff", color: "#17324D", borderColor: "rgba(8,127,191,0.2)" }}>
              {c.name}
            </button>
          ))}
        </div>

        <p className="text-sm mb-6" style={{ color: "#4A6B82" }} data-testid="product-count">
          {loading ? "Loading products..." : `${filtered.length} product${filtered.length === 1 ? "" : "s"} found`}
        </p>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-96 rounded-2xl" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-state">
            <PackageOpen className="w-14 h-14 mx-auto mb-4" style={{ color: "#16B8D4" }} />
            <h3 className="font-heading text-xl font-bold" style={{ color: "#063B63" }}>No products found</h3>
            <p className="mt-2 text-sm" style={{ color: "#4A6B82" }}>Try a different category or search term.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}
